
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOrganizationUsers, getOrganizationDetails } from '@/services/organizationUsersService';
import { User } from '@/types/user';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export function useOrganizationUsers() {
  const { profile } = useAuth();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const organizationId = profile?.organization_id;

  const { 
    data: users = [], 
    isLoading: loadingUsers, 
    refetch: refetchUsers 
  } = useQuery({
    queryKey: ['organization-users', organizationId, refreshTrigger],
    queryFn: () => organizationId ? getOrganizationUsers(organizationId) : Promise.resolve([]),
    enabled: !!organizationId,
  });

  const { 
    data: organization, 
    isLoading: loadingOrganization 
  } = useQuery({
    queryKey: ['organization-details', organizationId],
    queryFn: () => organizationId ? getOrganizationDetails(organizationId) : Promise.resolve(null),
    enabled: !!organizationId,
  });

  // Set up real-time subscription for organization_members table
  useEffect(() => {
    if (!organizationId) return;

    console.log('🔗 Setting up real-time subscription for organization_members...');
    const channel = supabase
      .channel('organization-members-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'organization_members',
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          console.log('🔴 Real-time update received for organization_members:', payload);
          setRefreshTrigger(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      console.log('🔌 Cleaning up real-time subscription for organization_members');
      supabase.removeChannel(channel);
    };
  }, [organizationId]);

  const handleUserUpdated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleUserInvited = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleManualRefresh = () => {
    refetchUsers();
  };

  return {
    users,
    organization,
    loading: loadingUsers || loadingOrganization,
    refreshing: false,
    handleUserUpdated,
    handleUserInvited,
    handleManualRefresh
  };
}
